import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { DraftsProvider } from "./contexts/DraftsContext";
import ProtectedRoute from "./components/ProtectedRoute";
import CustomerRoute from "./components/CustomerRoute";
import Home from "./pages/Home";
import Login from "./pages/Login";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DeleteAccount from "./pages/DeleteAccount";
import Signup from "./pages/Signup";
import DriverSignup from "./pages/DriverSignup";
import MujahezSignup from "./pages/MujahezSignup";
import Mujahez from "./pages/Mujahez";
import MujahezInventory from "./pages/MujahezInventory";
import SupervisorSignup from "./pages/SupervisorSignup";
import Supervisor from "./pages/Supervisor";
import ForgotPassword from "./pages/ForgotPassword";
import Cart from "./pages/Cart";
import OrderSuccess from "./pages/OrderSuccess";
import MyOrders from "./pages/MyOrders";
import Admin from "./pages/Admin";
import CategoryDetail from "./pages/CategoryDetail";
import Rewards from "./pages/Rewards";
import Driver from "./pages/Driver";
import Debts from "./pages/Debts";
import OrderTracking from "./pages/OrderTracking";
import Complaints from "./pages/Complaints";
import MyLocations from "./pages/MyLocations";
import MyAccount from "./pages/MyAccount";
import UpdateAvailableBanner from "./components/UpdateAvailableBanner";
import SupportFloatingButton from "./components/SupportFloatingButton";
import FloatingActions from "./components/FloatingActions";
import JumlatyBackground from "./components/JumlatyBackground";
import LoginCelebration from "./components/LoginCelebration";
import { useAuth } from "./contexts/AuthContext";

// Renders a one-shot celebration overlay when a customer just finished
// logging in or signing up. Pulled into its own component so it can call
// useAuth (which requires being inside <AuthProvider>).
const CelebrationLayer = () => {
  const { celebrateUser, dismissCelebration } = useAuth();
  if (!celebrateUser) return null;
  return <LoginCelebration user={celebrateUser} onClose={dismissCelebration} />;
};

const CustomerBackground = () => {
  const location = useLocation();
  // Show background on every authenticated portal (customer, driver, mujahez, admin),
  // hide only on auth screens (/login, /signup, etc.) — those have their own visuals.
  const hideOnPaths = ["/login", "/signup", "/driver-signup", "/mujahez-signup", "/supervisor-signup", "/forgot-password", "/sawakeen-jumlaty/signup", "/mujahez-jumlaty/signup", "/mushrif-jumlaty/signup"];
  if (hideOnPaths.some((p) => location.pathname === p)) return null;
  return <JumlatyBackground />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <DraftsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/driver-signup" element={<DriverSignup />} />
              <Route path="/sawakeen-jumlaty/signup" element={<DriverSignup />} />
              <Route path="/mujahez-signup" element={<MujahezSignup />} />
              <Route path="/mujahez-jumlaty/signup" element={<MujahezSignup />} />
              <Route path="/supervisor-signup" element={<SupervisorSignup />} />
              <Route path="/mushrif-jumlaty/signup" element={<SupervisorSignup />} />
              <Route
                path="/supervisor"
                element={
                  <ProtectedRoute>
                    <Supervisor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mushrif-jumlaty"
                element={
                  <ProtectedRoute>
                    <Supervisor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mandoob"
                element={
                  <ProtectedRoute>
                    <Supervisor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mujahez-jumlaty"
                element={
                  <ProtectedRoute>
                    <Mujahez />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mujahez-jumlaty/inventory"
                element={
                  <ProtectedRoute>
                    <MujahezInventory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mujahez-jumlaty/inventory/:id"
                element={
                  <ProtectedRoute>
                    <MujahezInventory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rep"
                element={<Navigate to="/mushrif-jumlaty" replace />}
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <Home />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/category/:categoryId"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <CategoryDetail />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cart"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <Cart />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-success/:orderId"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <OrderSuccess />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-orders"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <MyOrders />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-locations"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <MyLocations />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-account"
                element={
                  <ProtectedRoute>
                    <MyAccount />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/track/:orderId"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <OrderTracking />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rewards"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <Rewards />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/debts"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <Debts />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/complaints"
                element={
                  <ProtectedRoute>
                    <CustomerRoute>
                      <Complaints />
                    </CustomerRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/driver"
                element={
                  <ProtectedRoute>
                    <Driver />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sawakeen-jumlaty"
                element={
                  <ProtectedRoute>
                    <Driver />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute adminOnly>
                    <Admin />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <CustomerBackground />
            <FloatingActions />
            <CelebrationLayer />
            <UpdateAvailableBanner />
            <SupportFloatingButton />
          </BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
          </DraftsProvider>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
